import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { publicApi } from "@/lib/api";
import { IMAGES } from "@/lib/constants";

const SiteAssetsContext = createContext(null);

const DEFAULTS = {
  hero: IMAGES.hero,
  service_renovation: IMAGES.renovation,
  service_bathroom: IMAGES.bathrooms[2],
  service_solar_ac: IMAGES.solar_ac,
  service_painting: IMAGES.painting,
  service_ceramica: IMAGES.ceramica,
  about_team: IMAGES.about_team,
};

export function SiteAssetsProvider({ children }) {
  const [images, setImages] = useState(DEFAULTS);
  const [gallery, setGallery] = useState(
    IMAGES.bathrooms.map((url, i) => ({ id: `default-${i}`, data_url: url, caption: "", position: i }))
  );
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [imgRes, galRes] = await Promise.all([
        publicApi.get("/images"),
        publicApi.get("/gallery"),
      ]);
      const merged = { ...DEFAULTS };
      Object.entries(imgRes.data || {}).forEach(([key, val]) => {
        if (val && val.data_url) merged[key] = val.data_url;
      });
      setImages(merged);
      if (Array.isArray(galRes.data) && galRes.data.length > 0) {
        setGallery(galRes.data);
      }
    } catch (e) {
      // keep defaults
      // eslint-disable-next-line no-console
      console.warn("Could not load site assets", e?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(() => ({ images, gallery, loaded, reload: load }), [images, gallery, loaded, load]);
  return <SiteAssetsContext.Provider value={value}>{children}</SiteAssetsContext.Provider>;
}

export function useSiteAssets() {
  const ctx = useContext(SiteAssetsContext);
  if (!ctx) throw new Error("useSiteAssets must be used within SiteAssetsProvider");
  return ctx;
}
